import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart, Globe2, Users2, Award } from "lucide-react";
import { useRef, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import InfinityScrollAnimated from "@/components/shared/infinity-scroll-section";
import SectionBackground from "@/components/layout/SectionBackground";
import Tile from "@/components/shared/Tile";
import TileGrid from "@/components/shared/TileGrid";
import { theme } from "@/theme";
import aboutUsVideo from "@/assets/aboutpagecapcut.mp4";

const AboutUs = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  // Scroll-based zoom effect
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Zoom in effect - scales from 1 to 1.5
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.5]);
  
  // Fade out as zooming in
  const opacity = useTransform(scrollYProgress, [0, 0.8, 1], [1, 0.3, 0]);
  
  // Blur effect for depth - properly defined at top level
  const blur = useTransform(scrollYProgress, [0, 1], [0, 10]);
  const filter = useTransform(blur, (value) => `blur(${value}px)`);

  // Scroll indicator fade out - disappears instantly when user starts scrolling
  const scrollIndicatorOpacity = useTransform(scrollYProgress, [0, 0.01], [1, 0]);

  useEffect(() => {
    // Ensure video loads and plays
    const video = videoRef.current;
    if (video) {
      // Set video attributes
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      
      // Load and play
      video.load();
      
      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          // Video autoplay prevented - try again after user interaction
          const handleInteraction = async () => {
            try {
              await video.play();
              document.removeEventListener('click', handleInteraction);
              document.removeEventListener('touchstart', handleInteraction);
            } catch (err) {
              // Video play failed
            }
          };
          document.addEventListener('click', handleInteraction, { once: true });
          document.addEventListener('touchstart', handleInteraction, { once: true });
        }
      };

      // Wait for video to be loaded enough
      if (video.readyState >= 2) {
        playVideo();
      } else {
        video.addEventListener('loadeddata', playVideo, { once: true });
      }
    }
  }, []);

  const isMobile = useIsMobile();
  
  return (
    <main className={`relative min-h-screen pt-20 lg:pt-[88px] ${!isMobile ? 'bg-black' : ''}`}>
      {/* Header Section with Zoom Effect */}
      <motion.section 
        ref={heroRef}
        className="relative h-[100vh] md:h-[120vh] w-full overflow-hidden"
        style={{
          scale,
          opacity,
        }}
      >
        {/* Two Column Layout */}
        <div className="sticky top-0 h-screen w-full grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left Column - Content on Black Background */}
          <motion.div
            className="relative flex items-center justify-center px-4 sm:px-8 md:px-12 lg:px-16 py-8 md:py-12"
            style={{ 
              background: "linear-gradient(to right, rgba(0, 0, 0, 0.98) 0%, rgba(0, 0, 0, 0.95) 70%, rgba(0, 0, 0, 0.7) 100%)",
              zIndex: 10,
            }}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="w-full max-w-xl">
              {/* Main Title */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mb-6 leading-tight"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  fontSize: "clamp(28px, 8vw, 72px)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  color: theme.colors.text,
                  textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                }}
              >
                Inside the{" "}
                <span 
                  className="inline-block relative"
                  style={{ color: theme.colors.accent }}
                >
                  Endless Methodology
                  {/* Animated Underline */}
                  <motion.div
                    className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                    style={{ backgroundColor: theme.colors.accent }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                  />
                </span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="font-serif mb-8"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(14px, 3vw, 18px)",
                  color: "rgba(200, 200, 200, 0.85)",
                  letterSpacing: "0.08em",
                  fontWeight: 400,
                  textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
                }}
              >
                Klau y Ros Endless Methodology turns movement into a framework for continuous growth.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1 }}
                className="flex flex-col sm:flex-row gap-3 sm:gap-4"
              >
              <motion.a
                href="#bios"
                className="w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <button
                  className="relative overflow-hidden w-full sm:w-auto inline-flex items-center justify-center gap-2"
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: "#FFFFFF",
                    background: "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)",
                    border: "none",
                    borderRadius: "12px",
                    boxShadow: "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #F39C12 0%, #E67E22 100%)";
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(230, 126, 34, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)";
                    e.currentTarget.style.boxShadow = "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <span style={{ position: "relative", zIndex: 1 }}>Discover Their Journey</span>
                  <ArrowRight className="w-4 h-4" style={{ position: "relative", zIndex: 1 }} />
                </button>
              </motion.a>

              <motion.a
                href="/pricing"
                className="w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <button
                  className="relative overflow-hidden w-full sm:w-auto inline-flex items-center justify-center"
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: "#FFFFFF",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)";
                    e.currentTarget.style.boxShadow = "0 6px 28px rgba(255, 255, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <span style={{ position: "relative", zIndex: 1 }}>View Courses →</span>
                </button>
              </motion.a>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Video Background - Hidden on Mobile */}
          {!isMobile && (
            <motion.div 
              className="relative h-full overflow-hidden"
              style={{
                filter: isMobile ? 'none' : filter,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-contain"
                style={{ 
                  pointerEvents: 'none',
                  transform: isMobile ? 'scale(0.7)' : 'none'
                }}
              >
                <source src={aboutUsVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Gradient blend overlay for seamless transition */}
              <div
                className="absolute inset-0 hidden lg:block"
                style={{
                  background: "linear-gradient(to right, rgba(0, 0, 0, 0.4) 0%, transparent 20%)",
                }}
              />
              
              {/* Dark Overlay removed on mobile */}
              <div
                className="absolute inset-0 hidden lg:block"
                style={{
                  background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.5) 100%)",
                }}
              />
            </motion.div>
          )}
        </div>

        {/* Scroll Indicator - Fixed to viewport for initial visibility */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            className="fixed bottom-4 sm:bottom-8 md:bottom-12 left-1/2 transform -translate-x-1/2 cursor-pointer z-50"
            style={{ 
              zIndex: 50,
              opacity: scrollIndicatorOpacity,
              pointerEvents: 'auto'
            }}
            onClick={() => {
              // Scroll into the hero section to trigger dramatic zoom effect
              window.scrollBy({ top: window.innerHeight * 1.5, behavior: 'smooth' });
            }}
          >
            <div className="flex flex-col items-center gap-3">
              {/* Pulsing background glow */}
              <motion.div
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute w-20 h-20 rounded-full"
                style={{
                  background: "radial-gradient(circle, rgba(230, 126, 34, 0.3) 0%, transparent 70%)",
                  filter: "blur(15px)"
                }}
              />
              
              {/* Mouse scroll icon */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-7 h-11 border-2 rounded-full flex items-start justify-center p-2 backdrop-blur-md relative"
                style={{
                  borderColor: "rgba(230, 126, 34, 0.6)",
                  background: "rgba(0, 0, 0, 0.5)",
                  boxShadow: "0 0 20px rgba(230, 126, 34, 0.3)"
                }}
              >
                <motion.div
                  animate={{ y: [0, 14, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-1.5 h-3 rounded-full"
                  style={{
                    background: "linear-gradient(to bottom, rgba(230, 126, 34, 0.9), rgba(243, 156, 18, 0.6))"
                  }}
                />
              </motion.div>
              
              {/* Text with subtle glow */}
              <motion.p
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="text-white text-sm font-semibold tracking-wider relative"
                style={{
                  textShadow: "0 0 10px rgba(230, 126, 34, 0.5), 0 2px 4px rgba(0, 0, 0, 0.5)"
                }}
              >
                SCROLL TO DISCOVER
              </motion.p>
              
              {/* Down arrow */}
              <motion.div
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
              >
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 20 20" 
                  fill="none"
                  style={{
                    filter: "drop-shadow(0 0 8px rgba(230, 126, 34, 0.6))"
                  }}
                >
                  <path 
                    d="M10 3L10 15M10 15L5 10M10 15L15 10" 
                    stroke="rgba(230, 126, 34, 0.9)" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
            </div>
          </motion.div>
      </motion.section>

      {/* Scroll Prompt Section */}
      <section className="relative py-2 md:py-4 bg-black -mt-20">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Animated scroll icon */}
            <motion.div
              animate={{ 
                y: [0, 15, 0],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-block mb-4"
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md"
                style={{
                  background: `radial-gradient(circle, ${theme.colors.accent}30, ${theme.colors.accent}10)`,
                  border: `2px solid ${theme.colors.accent}60`,
                  boxShadow: `0 0 20px ${theme.colors.accent}40`
                }}
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none"
                  className="text-orange-400"
                >
                  <path 
                    d="M12 5L12 19M12 19L6 13M12 19L18 13" 
                    stroke="currentColor"
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </motion.div>

            {/* Text prompt */}
            <motion.h3
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl lg:text-3xl font-bold mb-2"
              style={{
                fontFamily: "'Grift', sans-serif",
                color: theme.colors.text,
              }}
            >
              Discover the{" "}
              <span style={{ color: theme.colors.accent }}>Endless Methodology</span>
            </motion.h3>
            
            {!isMobile && (
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg mb-2"
                style={{
                  color: theme.colors.secondary,
                }}
              >
                Scroll to explore the four pillars that shape every movement
              </motion.p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Infinity Scroll Animated Section */}
      <div className="relative -mt-16">
        <InfinityScrollAnimated />
      </div>

      {/* Bio Section - Klau */}
      <section id="bios" className="relative py-12 md:py-16 lg:py-20 px-4">
        <SectionBackground />
        
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center mb-16 md:mb-24 lg:mb-32"
          >
            {/* Image Side */}
            <div className="relative order-2 md:order-1">
              <div className="aspect-[3/4] rounded-2xl md:rounded-3xl overflow-hidden relative group">
                {/* NOTE: Image should be optimized to ~1200x1600px (3:4 aspect ratio, 2x retina: 2400x3200px) for aspect-[3/4] display */}
                <img
                  src="/assets/klau-photo-2.jpg"
                  alt="Klau - Bachata Instructor"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  width={1200}
                  height={1600}
                />
                {/* Border effect */}
                <div 
                  className="absolute inset-0 rounded-3xl pointer-events-none"
                  style={{
                    border: `1px solid ${theme.colors.accent}40`,
                    boxShadow: `inset 0 0 60px ${theme.colors.accent}10`,
                  }}
                />
                {/* Glow */}
                <div 
                  className="absolute -inset-6 blur-3xl -z-10 opacity-50"
                  style={{
                    background: `radial-gradient(circle at center, ${theme.colors.accent}40, transparent 70%)`,
                  }}
                />
              </div>
            </div>

            {/* Text Side */}
            <div className="order-1 md:order-2">
              {/* Name & Title */}
              <div className="mb-8">
                <div
                  className="inline-block px-4 py-1.5 rounded-full mb-4 text-xs font-semibold tracking-wider uppercase"
                  style={{
                    backgroundColor: `${theme.colors.accent}20`,
                    color: theme.colors.accent,
                    border: `1px solid ${theme.colors.accent}40`,
                  }}
                >
                  Co-Founder
                </div>
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3"
                  style={{ color: theme.colors.text }}
                >
                  Klaudia Perdek
                </h2>
                <p
                  className="text-lg sm:text-xl md:text-2xl italic mb-6"
                  style={{ color: theme.colors.accent }}
                >
                  From Poland with Precision
                </p>
              </div>

              {/* Bio Text */}
              <div className="space-y-4 mb-8">
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: theme.colors.secondary }}
                >
                  Born in Poland in 2000, Klaudia began dancing at age 8, training in ballet, salsa, 
                  cheerleading, and bachata. By 10, she was competing nationally and internationally.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: theme.colors.secondary }}
                >
                  Since discovering bachata in 2017, she has gained international recognition, 
                  achieving podium finishes at events like the World Latin Cup and BachataArt 
                  World Championship.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: theme.colors.secondary }}
                >
                  She now teaches globally across Europe, Asia, and the Americas, bringing her 
                  technical precision and competitive experience to dancers of all levels.
                </p>
              </div>

              {/* Stats */}
              <TileGrid columns={2} gap="sm" className="sm:grid-cols-3">
                <Tile variant="glass" size="sm" noPadding className="text-center">
                  <div className="p-4">
                    <Award size={24} style={{ color: theme.colors.accent }} className="mx-auto mb-2" />
                    <div className="text-xl md:text-2xl font-bold mb-1" style={{ color: theme.colors.accent }}>17+</div>
                    <div className="text-xs" style={{ color: theme.colors.textMuted }}>Years Dancing</div>
                  </div>
                </Tile>
                <Tile variant="glass" size="sm" noPadding className="text-center">
                  <div className="p-4">
                    <Globe2 size={24} style={{ color: theme.colors.accent }} className="mx-auto mb-2" />
                    <div className="text-2xl font-bold mb-1" style={{ color: theme.colors.accent }}>3</div>
                    <div className="text-xs" style={{ color: theme.colors.textMuted }}>Continents</div>
                  </div>
                </Tile>
                <Tile variant="glass" size="sm" noPadding className="text-center">
                  <div className="p-4">
                    <Users2 size={24} style={{ color: theme.colors.accent }} className="mx-auto mb-2" />
                    <div className="text-2xl font-bold mb-1" style={{ color: theme.colors.accent }}>8+</div>
                    <div className="text-xs" style={{ color: theme.colors.textMuted }}>Years Bachata</div>
                  </div>
                </Tile>
              </TileGrid>
            </div>
          </motion.div>

          {/* Teaching Philosophy Section - Klau */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="my-16 md:my-24 lg:my-32 rounded-xl md:rounded-2xl overflow-hidden"
            style={{
              background: "rgba(0, 0, 0, 0.6)",
              border: `1px solid ${theme.colors.accent}20`,
              backdropFilter: "blur(10px)",
            }}
          >
            <div className="p-4 sm:p-6 md:p-10 lg:p-16">
              {/* Section Title with Accent Line */}
              <div className="mb-8">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "4rem" }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="h-1 mb-6"
                  style={{
                    background: `linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                  }}
                />
                <h3
                  className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: theme.colors.text,
                  }}
                >
                  Teaching Philosophy
                </h3>
              </div>

              {/* Philosophy Text */}
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 md:mb-12 max-w-4xl"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: theme.colors.secondary,
                }}
              >
                Klaudia's teaching is grounded in structure, musicality, and emotional awareness. 
                Her goal is to help dancers build confidence, elegance, and technical strength 
                through discipline and expression.
              </motion.p>

              {/* Core Goals Title */}
              <h4
                className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6 md:mb-8"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: theme.colors.text,
                }}
              >
                Core Goals
              </h4>

              {/* Goals List */}
              <div className="space-y-4 md:space-y-6">
                {[
                  "Inspire women to explore confidence through movement.",
                  "Deliver structured, progressive learning methods.",
                  "Cultivate authentic community online and offline.",
                  "Merge discipline and artistry for growth.",
                  "Champion passion, power, and vulnerability on stage."
                ].map((goal, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="flex items-start gap-3 sm:gap-4 md:gap-6"
                  >
                    {/* Number */}
                    <span
                      className="text-2xl sm:text-3xl md:text-4xl font-bold flex-shrink-0"
                      style={{
                        fontFamily: "'Grift', sans-serif",
                        background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}.
                    </span>
                    {/* Goal Text */}
                    <p
                      className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed pt-1"
                      style={{
                        fontFamily: "'Grift', sans-serif",
                        color: theme.colors.secondary,
                      }}
                    >
                      {goal}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Bio Section - Ros */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center"
          >
            {/* Text Side */}
            <div>
              {/* Name & Title */}
              <div className="mb-8">
                <div
                  className="inline-block px-4 py-1.5 rounded-full mb-4 text-xs font-semibold tracking-wider uppercase"
                  style={{
                    backgroundColor: `${theme.colors.accent}20`,
                    color: theme.colors.accent,
                    border: `1px solid ${theme.colors.accent}40`,
                  }}
                >
                  Co-Founder
                </div>
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3"
                  style={{ color: theme.colors.text }}
                >
                  Adrián Ros
                </h2>
                <p
                  className="text-lg sm:text-xl md:text-2xl italic mb-6"
                  style={{ color: theme.colors.accent }}
                >
                  From Spain with Soul
                </p>
              </div>

              {/* Bio Text */}
              <div className="space-y-4 mb-8">
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: theme.colors.secondary }}
                >
                  Born in Galicia, Spain, in 1993, Adrián began dancing in 2005 with a foundation 
                  in sport dance and Cuban salsa. In 2014, he earned certification in the Island 
                  Touch methodology and later refined his technique in Madrid under top professionals.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: theme.colors.secondary }}
                >
                  He went on to develop Endless Bachata, a signature style defined by smooth transitions, 
                  musical connection, and emotional storytelling.
                </p>
              </div>

              {/* Stats */}
              <TileGrid columns={2} gap="sm" className="sm:grid-cols-3">
                <Tile variant="glass" size="sm" noPadding className="text-center">
                  <div className="p-4">
                    <Award size={24} style={{ color: theme.colors.accent }} className="mx-auto mb-2" />
                    <div className="text-xl md:text-2xl font-bold mb-1" style={{ color: theme.colors.accent }}>20+</div>
                    <div className="text-xs" style={{ color: theme.colors.textMuted }}>Years Dancing</div>
                  </div>
                </Tile>
                <Tile variant="glass" size="sm" noPadding className="text-center">
                  <div className="p-4">
                    <Globe2 size={24} style={{ color: theme.colors.accent }} className="mx-auto mb-2" />
                    <div className="text-2xl font-bold mb-1" style={{ color: theme.colors.accent }}>3</div>
                    <div className="text-xs" style={{ color: theme.colors.textMuted }}>Continents</div>
                  </div>
                </Tile>
                <Tile variant="glass" size="sm" noPadding className="text-center">
                  <div className="p-4">
                    <Users2 size={24} style={{ color: theme.colors.accent }} className="mx-auto mb-2" />
                    <div className="text-2xl font-bold mb-1" style={{ color: theme.colors.accent }}>10+</div>
                    <div className="text-xs" style={{ color: theme.colors.textMuted }}>Years Bachata</div>
                  </div>
                </Tile>
              </TileGrid>
            </div>

            {/* Image Side */}
            <div className="relative">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden relative group">
                {/* NOTE: Image should be optimized to ~1200x1600px (3:4 aspect ratio, 2x retina: 2400x3200px) for aspect-[3/4] display */}
                <img
                  src="/assets/ros-photo-3.jpg"
                  alt="Ros - Bachata Instructor"
                  loading="lazy"
                  width={1200}
                  height={1600}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Border effect */}
                <div 
                  className="absolute inset-0 rounded-3xl pointer-events-none"
                  style={{
                    border: `1px solid ${theme.colors.accent}40`,
                    boxShadow: `inset 0 0 60px ${theme.colors.accent}10`,
                  }}
                />
                {/* Glow */}
                <div 
                  className="absolute -inset-6 blur-3xl -z-10 opacity-50"
                  style={{
                    background: `radial-gradient(circle at center, ${theme.colors.accent}40, transparent 70%)`,
                  }}
                />
              </div>
            </div>
          </motion.div>

          {/* Teaching Philosophy Section - Ros */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mt-16 md:mt-24 lg:mt-32 rounded-xl md:rounded-2xl overflow-hidden"
            style={{
              background: "rgba(0, 0, 0, 0.6)",
              border: `1px solid ${theme.colors.accent}20`,
              backdropFilter: "blur(10px)",
            }}
          >
            <div className="p-4 sm:p-6 md:p-10 lg:p-16">
              {/* Section Title with Accent Line */}
              <div className="mb-8">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "4rem" }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="h-1 mb-6"
                  style={{
                    background: `linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                  }}
                />
                <h3
                  className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: theme.colors.text,
                  }}
                >
                  Teaching Philosophy
                </h3>
              </div>

              {/* Philosophy Text */}
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 md:mb-12 max-w-4xl"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: theme.colors.secondary,
                }}
              >
                Adrián's philosophy centers on emotional connection and musical storytelling. 
                Drawing from his roots in sport dance and Cuban salsa, he helps dancers move 
                beyond choreography — guiding them to express intention, energy, and authenticity 
                through every movement.
              </motion.p>

              {/* Core Goals Title */}
              <h4
                className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6 md:mb-8"
                style={{
                  fontFamily: "'Grift', sans-serif",
                  color: theme.colors.text,
                }}
              >
                Core Goals
              </h4>

              {/* Goals List */}
              <div className="space-y-4 md:space-y-6">
                {[
                  "Teach dancers to connect beyond the steps through energy and intention.",
                  "Provide tools to enhance musicality, fluidity, and movement freedom.",
                  "Build choreographies that are emotionally powerful and technically sharp.",
                  "Support both beginners and professionals in their artistic journey.",
                  "Use humor, empathy, and challenge to create unforgettable classes."
                ].map((goal, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="flex items-start gap-3 sm:gap-4 md:gap-6"
                  >
                    {/* Number */}
                    <span
                      className="text-2xl sm:text-3xl md:text-4xl font-bold flex-shrink-0"
                      style={{
                        fontFamily: "'Grift', sans-serif",
                        background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}.
                    </span>
                    {/* Goal Text */}
                    <p
                      className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed pt-1"
                      style={{
                        fontFamily: "'Grift', sans-serif",
                        color: theme.colors.secondary,
                      }}
                    >
                      {goal}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Together Section */}
      <section className="relative py-12 md:py-16 lg:py-20 px-4">
        <SectionBackground />
        
        <div className="relative max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full mb-6 md:mb-8"
              style={{
                backgroundColor: `${theme.colors.accent}20`,
                border: `2px solid ${theme.colors.accent}40`,
              }}
            >
              <Heart size={32} className="md:w-10 md:h-10" style={{ color: theme.colors.accent }} />
            </div>

            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6"
              style={{ color: theme.colors.text }}
            >
              Together, Creating{" "}
              <span style={{ color: theme.colors.accent }}>Endless Magic</span>
            </h2>

            <p
              className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 md:mb-12 leading-relaxed max-w-3xl mx-auto"
              style={{ color: theme.colors.secondary }}
            >
              When Klau and Ros began working together, they recognized a shared commitment to elevating Bachata through structure, authenticity, and artistic purpose.
            </p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-8 md:mt-12 lg:mt-16"
            >
              <a
                href="/pricing"
                className="theme-button-primary inline-flex items-center gap-2 group text-sm sm:text-base md:text-lg"
                style={{
                  padding: "12px 28px",
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "12px",
                }}
              >
                <span>Start Learning with Us</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;

